/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip as CustomToolTip } from 'react-tooltip';

import HelpIcon from 'components/common/icons/HelpIcon';
import CloseIcon from 'components/common/icons/CloseIcon';

type PLACES =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'right'
  | 'right-start'
  | 'right-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end';

/**
 * 'button_or_error':
 *  - Used for tooltip that overlays for errors or button.
 *  - variant that has smaller padding value than 'default'
 *  - Removes margin right for main text field.
 */
export type ToolTipVariant = 'default' | 'button_or_error';

export interface ToolTipProps {
  placement?: PLACES;
  text?: string | any;
  title?: string;
  toolTipId: string;
  showCloseButton?: boolean;
  showTooltipOnClick?: boolean;
  ml?: number;
  mt?: number;
  noArrow?: boolean;
  // Tooltip getting used as spotlight have different style triggers 'handleClose()' handler
  spotLight?: boolean;
  // Custom css selctor to where to show tooltip from on children component.
  childSelector?: string;
  showIcon?: boolean;
  iconComponent?: JSX.Element;
  maxWidth?: string;
  handleClose?: () => void;
  variant?: ToolTipVariant;
  linkComponent?: ReactNode;
}

/**
 * For normal:
 *  learn more should be in-line.
 *  spotlight: keep separate line.
 *
 * x button:
 *  click away should also close.
 * @param param0
 * @returns
 */
const Tooltip: FC<React.PropsWithChildren<ToolTipProps>> = ({
  text,
  children,
  toolTipId = 'epoch-tooltip',
  placement = 'top',
  childSelector = 'div',
  ml = 4,
  mt = 0,
  title,
  showCloseButton = false,
  showTooltipOnClick = false,
  noArrow = false,
  spotLight = false,
  showIcon = true,
  iconComponent = <HelpIcon />,
  maxWidth = '336px',
  variant = 'default',
  linkComponent = null,
  handleClose
}) => {
  // If showByclicking is on, isOpen should be initialized to false
  // If spotLight is on, isOpen should be initialized to true
  const [isOpen, setIsOpen] = useState(spotLight && !showTooltipOnClick);
  const newRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  useEffect(() => {
    if (spotLight && !isOpen && handleClose) {
      handleClose();
    }
  }, [isOpen, spotLight]);

  const selector = children
    ? `div[id='${toolTipId}-body'] > ${childSelector}:first-child`
    : `#${toolTipId}`;

  const renderContent = (): null | JSX.Element => {
    if (children) {
      return <div id={`${toolTipId}-body`}>{children}</div>;
    } else if (showIcon) {
      return iconComponent;
    }
    return null;
  };

  /**
   * Close tooltip if user clicks outside of tooltip box.
   * This handles spotlight tooltip where we want tooltip to be shown until user clicks 'x' or
   * outside of tooltip box.
   * @param e
   */
  const handleOutsideClick = (e: Event): void => {
    if (newRef.current && !newRef.current.contains(e.target as HTMLDivElement)) {
      setIsOpen(false);
    }
  };

  /**
   * Grab correct padding value based on 'variant' passed in.
   */
  const paddingValue = useMemo(() => {
    if (variant === 'button_or_error') {
      return {
        top: 4,
        bottom: 4,
        left: 8,
        right: 8
      };
    }
    return {
      top: 12,
      bottom: 12,
      left: 12,
      right: 12
    };
  }, [variant]);

  return (
    <div style={{ marginLeft: ml, marginTop: mt }}>
      <a
        id={toolTipId}
        onMouseEnter={(): void => {
          if (!spotLight && !showTooltipOnClick) {
            setIsOpen(true);
          }
        }}
        onClick={(): void => {
          if (showTooltipOnClick) {
            setIsOpen(true);
          }
        }}
      >
        {renderContent()}
      </a>
      <div ref={newRef}>
        <CustomToolTip
          place={placement}
          isOpen={showCloseButton || showTooltipOnClick ? isOpen : undefined}
          anchorSelect={selector}
          noArrow={noArrow}
          style={{
            maxWidth,
            backgroundColor: 'black',
            paddingBottom: paddingValue.bottom,
            paddingTop: paddingValue.top,
            paddingLeft: paddingValue.left,
            paddingRight: paddingValue.right,
            zIndex: 10,
            borderRadius: 4
          }}
          opacity={1}
          clickable
        >
          {isOpen && (
            <div>
              <div className="text-wrap tw-flex tw-flex-row">
                <div className={variant === 'default' ? 'tw-mr-2' : 'tw-mr-0'}>
                  {title && <p className="tw-text-xs tw-font-medium tw-leading-4"> {title}</p>}
                  <p className="tw-mb-0 tw-text-xs tw-font-normal tw-leading-4">
                    {text}
                    {linkComponent}
                  </p>
                </div>
                {showCloseButton && (
                  <div className="tw--mt-1 tw-ml-auto">
                    <button
                      className="tw-p-0 "
                      onClick={(): void => setIsOpen(false)}
                      type="button"
                    >
                      <CloseIcon color="white" width={16} height={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </CustomToolTip>
      </div>
    </div>
  );
};

export default Tooltip;
